import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';
import type { ListModulePaginationMeta } from 'src/app/shared/components/list-module';

export type SubscriptionType = 'STANDARD' | 'CUSTOM';

export interface SubscriptionTenantLink {
  id: number;
  uuid?: string | null;
  subscription_id: number;
  tenant_id: number;
  is_active?: boolean | null;
  is_deleted?: boolean | null;
  created_at?: string | null;
  tenant?: {
    id: number;
    tenant_name: string | null;
    tenant_subdomain: string | null;
    email?: string | null;
    status?: string | null;
  } | null;
}

export interface SubscriptionRow {
  id: number;
  uuid?: string | null;
  subscription_name: string | null;
  subscription_type: string | null;
  subscription_price: number | string | null;
  no_of_subscription_models?: Record<string, unknown> | null;
  validity_days: number | null;
  is_active: boolean | null;
  is_deleted?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
  created_by?: number | null;
  updated_by?: number | null;
  subscriptionTenants?: SubscriptionTenantLink[];
}

export interface SubscriptionListPayload {
  result: SubscriptionRow[];
  meta: ListModulePaginationMeta & { prev?: number | null; next?: number | null };
}

export interface SubscriptionListQuery {
  page?: number;
  page_size?: number;
  searchText?: string;
  status?: 'active' | 'inactive' | 'deleted';
  subscription_type?: SubscriptionType;
  sortColumn?: string;
  sortBy?: 'ASC' | 'DESC';
  is_active?: boolean;
}

export interface CreateSubscriptionPayload {
  subscription_name: string;
  subscription_type: SubscriptionType;
  subscription_price?: number;
  no_of_subscription_models?: Record<string, unknown>;
  validity_days?: number;
  is_active?: boolean;
  tenant_ids?: number[];
}

export type UpdateSubscriptionPayload = Partial<CreateSubscriptionPayload>;

@Injectable({ providedIn: 'root' })
export class SubscriptionApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  listSubscriptions(
    query: SubscriptionListQuery = {},
  ): Observable<ApiEnvelope<SubscriptionListPayload>> {
    let params = new HttpParams();
    params = params.set('page', String(query.page ?? 1));
    params = params.set('page_size', String(query.page_size ?? 10));
    if (query.searchText?.trim()) {
      params = params.set('searchText', query.searchText.trim());
    }
    if (query.status) {
      params = params.set('status', query.status);
    }
    if (query.subscription_type) {
      params = params.set('subscription_type', query.subscription_type);
    }
    if (query.sortColumn) {
      params = params.set('sortColumn', query.sortColumn);
    }
    if (query.sortBy) {
      params = params.set('sortBy', query.sortBy);
    }
    if (query.is_active !== undefined) {
      params = params.set('is_active', String(query.is_active));
    }
    return this.http.get<ApiEnvelope<SubscriptionListPayload>>(
      `${this.base}/subscription`,
      { params },
    );
  }

  getById(id: number): Observable<ApiEnvelope<SubscriptionRow>> {
    return this.http.get<ApiEnvelope<SubscriptionRow>>(
      `${this.base}/subscription/${id}`,
    );
  }

  create(
    body: CreateSubscriptionPayload,
  ): Observable<ApiEnvelope<SubscriptionRow>> {
    return this.http.post<ApiEnvelope<SubscriptionRow>>(
      `${this.base}/subscription`,
      body,
    );
  }

  update(
    id: number,
    body: UpdateSubscriptionPayload,
  ): Observable<ApiEnvelope<SubscriptionRow>> {
    return this.http.patch<ApiEnvelope<SubscriptionRow>>(
      `${this.base}/subscription/${id}`,
      body,
    );
  }

  softDelete(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.delete<ApiEnvelope<unknown>>(
      `${this.base}/subscription/${id}`,
    );
  }

  restore(id: number): Observable<ApiEnvelope<SubscriptionRow>> {
    return this.http.patch<ApiEnvelope<SubscriptionRow>>(
      `${this.base}/subscription/${id}/restore`,
      {},
    );
  }

  hardDelete(id: number): Observable<ApiEnvelope<unknown>> {
    return this.http.delete<ApiEnvelope<unknown>>(
      `${this.base}/subscription/permanent-delete/${id}`,
    );
  }
}
